import { useEffect, useRef, useState } from 'react'
import { UserPlus, X } from 'lucide-react'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { useFamilyStore } from '../../stores/familyStore'
import { usePermissions } from '../../hooks/usePermissions'
import { useApiService } from '../../services/api'
import { track } from '../../utils/analytics'
import {
  shouldShowInviteCard,
  readDismissals,
  saveDismissals,
  recordDismissal,
  type InviteCardDismissals,
} from '../../utils/inviteCard'

interface InviteSecondParentCardProps {
  // Z kalendarza, który już trzyma useEntitlements — drugie wywołanie hooka dubluje polling
  isPaying: boolean
}

const NO_DISMISSALS: InviteCardDismissals = { count: 0, lastDismissedAt: null }

// Zachęta do zaproszenia drugiego rodzica nad kalendarzem (reguły w utils/inviteCard.ts)
export function InviteSecondParentCard({ isPaying }: InviteSecondParentCardProps) {
  const family = useFamilyStore(state => state.family)
  const members = useFamilyStore(state => state.members)
  const { isParentA } = usePermissions()
  const apiService = useApiService()

  const familyId = family?.id
  const hasSecondParent = members.some(m => m.role === 'parent_b')

  const [dismissals, setDismissals] = useState<InviteCardDismissals>(NO_DISMISSALS)
  // null = jeszcze nie wiemy; bez odpowiedzi z listy zaproszeń karty nie pokazujemy
  const [hasPendingInvite, setHasPendingInvite] = useState<boolean | null>(null)
  const [email, setEmail] = useState('')
  const [sending, setSending] = useState(false)
  const [sentTo, setSentTo] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const shownTrackedFor = useRef<string | null>(null)

  useEffect(() => {
    if (familyId) setDismissals(readDismissals(familyId))
  }, [familyId])

  // Listę zaproszeń pobieramy tylko, gdy karta w ogóle wchodzi w grę
  useEffect(() => {
    if (!familyId || !isParentA || hasSecondParent) return
    let cancelled = false
    apiService.getInvitations(familyId)
      .then(res => {
        if (cancelled) return
        const nowTs = Date.now()
        setHasPendingInvite((res.invitations ?? []).some(inv =>
          inv.role === 'parent_b' &&
          inv.status === 'pending' &&
          new Date(inv.expires_at).getTime() > nowTs
        ))
      })
      .catch(() => { /* bez listy zaproszeń karta zostaje ukryta */ })
    return () => { cancelled = true }
  }, [familyId, isParentA, hasSecondParent, apiService])

  const visible = !!familyId && hasPendingInvite !== null && shouldShowInviteCard({
    isParentA,
    hasSecondParent,
    hasPendingParentInvite: hasPendingInvite,
    isPaying,
    dismissals,
    now: new Date(),
  })

  useEffect(() => {
    if (visible && familyId && shownTrackedFor.current !== familyId) {
      shownTrackedFor.current = familyId
      track('invite_card_shown', { dismissals: dismissals.count })
    }
  }, [visible, familyId, dismissals.count])

  if (!familyId || (!visible && !sentTo)) return null

  const handleDismiss = () => {
    const next = recordDismissal(dismissals, new Date())
    saveDismissals(familyId, next)
    setDismissals(next)
    setSentTo(null)
    track('invite_card_dismissed', { dismissals: next.count, paying: isPaying })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = email.trim()
    if (!trimmed) return
    setSending(true)
    setError(null)
    try {
      await apiService.createInvitation({ email: trimmed, role: 'parent_b', familyId })
      track('invite_sent', { role: 'parent_b', source: 'calendar_card' })
      setSentTo(trimmed)
      setEmail('')
      setHasPendingInvite(true)
    } catch (err: any) {
      setError(err?.message || 'Nie udało się wysłać zaproszenia. Spróbuj ponownie.')
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="mx-2 sm:mx-0 mb-2 sm:mb-3 rounded-xl border border-slate-200 bg-white/80 backdrop-blur-sm p-3 sm:p-4 shadow-sm">
      <div className="flex items-start gap-3">
        <UserPlus className="h-5 w-5 mt-0.5 shrink-0 text-blue-600" aria-hidden="true" />
        <div className="flex-1 min-w-0">
          {sentTo ? (
            <p className="text-sm text-slate-700">
              Zaproszenie wysłane na <span className="font-medium break-all">{sentTo}</span>. Gdy drugi rodzic je przyjmie, zobaczycie wspólny kalendarz.
            </p>
          ) : (
            <>
              <h2 className="text-sm font-semibold text-slate-900">Zaproś drugiego rodzica</h2>
              <p className="text-sm text-slate-600 mt-0.5">
                Kalendarz działa najlepiej, gdy oboje widzicie plan i zmiany. Drugi rodzic dostanie e-mail z linkiem do dołączenia.
              </p>
              <form onSubmit={handleSubmit} className="mt-2 flex flex-col sm:flex-row gap-2">
                <Input
                  type="email"
                  required
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="E-mail drugiego rodzica"
                  aria-label="E-mail drugiego rodzica"
                  autoComplete="off"
                  className="sm:max-w-xs bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:border-blue-400 focus:ring-2 focus:ring-blue-400 focus:ring-opacity-20"
                  disabled={sending}
                />
                <Button type="submit" className="neon-button text-white whitespace-nowrap" disabled={sending || !email.trim()}>
                  {sending ? 'Wysyłanie…' : 'Wyślij zaproszenie'}
                </Button>
              </form>
              {error && <p className="text-sm text-red-600 mt-2" role="alert">{error}</p>}
            </>
          )}
        </div>
        {!sentTo && (
          <button
            type="button"
            onClick={handleDismiss}
            className="shrink-0 rounded-md p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100"
            aria-label="Zamknij"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  )
}
